import { BOTONES } from '../../../../../componentes/botones';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  ViewEncapsulation,
  computed,
  inject,
  resource,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IResumenAplicacionPerfil } from 'modelos/src';
import { ConfigsDeseadasService } from '../../../../../auxiliares/servicios/http/configs-deseadas.service';
import { LoginService } from '../../../../login/login.service';
import { StatCardComponent } from '../../../../../componentes/stat-card/stat-card.component';
import { CardColor } from '../../../../../componentes/componentes.interfaces';
import {
  NombresSeguimientoService,
  nombreUsuario,
} from '../nombres-seguimiento.service';
import {
  AvanceAplicacion,
  CADENCIA,
  MOTIVOS_APLICACION,
  TONO,
  avanceResumen,
  clienteSinMarca,
  esSinMarca,
  estadoAplicacion,
  fechaHora,
  haceCuanto,
  queSeHizo,
  textoFuente,
} from '../seguimiento.helpers';

/** Cuánto hacia atrás mira la tabla. El backend usa lo mismo por defecto. */
const DIAS = 30;

type Resumen = IResumenAplicacionPerfil & { id: string };

interface Fila {
  r: Resumen;
  a: AvanceAplicacion;
  titulo: string;
  icono: string;
  motivo: string;
  motivoDetalle: string;
  quien?: string;
  cliente: string;
  jerarquia: string;
  porcentaje: number;
  tono: string;
  estado: string;
  iconoEstado: string;
  terminada: boolean;
  sinMarca: boolean;
}

const SEGMENTOS: {
  clave: keyof AvanceAplicacion;
  etiqueta: string;
  tono: string;
  ayuda: string;
}[] = [
  {
    clave: 'aplicado',
    etiqueta: 'con el perfil',
    tono: TONO.ok,
    ayuda: 'El nodo ya tiene la configuración que pide el perfil.',
  },
  {
    clave: 'enCurso',
    etiqueta: 'en curso',
    tono: TONO.info,
    ayuda:
      'Todavía no tienen la configuración: están en la fila de envío, esperando que el nodo confirme o sin comunicación. El detalle dice cuál.',
  },
  {
    clave: 'detenido',
    etiqueta: 'detenidas',
    tono: TONO.aviso,
    ayuda:
      'Se les mandó el perfil las veces previstas y no quedó aplicado. Se deja de insistir unas horas y después se reintenta solo.',
  },
];

/**
 * Las aplicaciones de perfil: cada acción que mandó perfiles a luminarias y
 * cuántas ya quedaron con él.
 *
 * Una acción no es un envío: asignar un perfil a un grupo de 1.500 luminarias
 * sale en muchas tandas; acá es una sola ficha, y su avance cuenta luminarias
 * con la configuración puesta, no envíos confirmados. Entra sólo lo que cambió.
 */
@Component({
  selector: 'app-listado-aplicaciones',
  standalone: true,
  imports: [
    DecimalPipe,
    NgTemplateOutlet,
    BOTONES,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    RouterLink,
    StatCardComponent,
  ],
  templateUrl: './listado-aplicaciones.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class ListadoAplicacionesComponent {
  private readonly http = inject(ConfigsDeseadasService);
  private readonly nombres = inject(NombresSeguimientoService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly query = toSignal(this.route.queryParamMap, {
    initialValue: undefined,
  });

  /** Sólo se ofrece ver lo de los clientes hijos a quien tiene hijos. */
  protected readonly puedeVerHijos =
    LoginService.getCliente()?.tipoCliente !== 'Final';

  /**
   * `?fuente=<id>`: sólo las acciones que salieron de un grupo, una puesta o una
   * luminaria. Es a dónde lleva "Seguimiento del perfil" desde sus detalles.
   */
  protected readonly fuente = computed(() => this.query()?.get('fuente') ?? '');

  /** Por defecto suma lo de los clientes hijos; `?hijos=0` lo limita al propio. */
  protected readonly hijos = computed(
    () => this.puedeVerHijos && this.query()?.get('hijos') !== '0',
  );

  protected readonly dias = DIAS;
  protected readonly SEGMENTOS = SEGMENTOS;

  // Polleo con reload(): un cambio en los params dejaría el valor en undefined
  // en cada vuelta y la lista se vaciaría y redibujaría entera.
  constructor() {
    const intervalo = setInterval(() => {
      if (document.visibilityState === 'visible') this.aplicaciones.reload();
    }, CADENCIA.listado * 1000);
    inject(DestroyRef).onDestroy(() => clearInterval(intervalo));
  }

  protected readonly aplicaciones = resource({
    params: () => ({ hijos: this.hijos() }),
    loader: ({ params }) =>
      this.http.aplicaciones({ includeChildren: params.hijos || undefined }),
  });

  /** Nombres de quienes hicieron cada acción. */
  private readonly usuarios = resource({
    params: () => {
      const ids = new Set<string>();
      for (const r of this.aplicaciones.value() ?? []) {
        if (r.aplicacion?.idUsuario) ids.add(r.aplicacion.idUsuario);
      }
      return [...ids].sort().join(',');
    },
    loader: ({ params }) =>
      this.nombres.usuarios(params ? params.split(',') : []),
  });

  private readonly filas = computed<Fila[]>(() => {
    const fuente = this.fuente();
    const todas = this.aplicaciones.value() ?? [];
    return (
      fuente ? todas.filter((r) => r.aplicacion?.fuente?.id === fuente) : todas
    ).map((r) => this.fila(r));
  });

  /** Cómo se llama la fuente del filtro, tomado de la marca de sus acciones. */
  protected readonly nombreFuente = computed(() => {
    const fuente = this.fuente();
    if (!fuente) return undefined;
    const a = (this.aplicaciones.value() ?? []).find(
      (r) => r.aplicacion?.fuente?.id === fuente,
    )?.aplicacion;
    return a ? textoFuente(a) : 'esta fuente';
  });

  protected readonly enCurso = computed(() =>
    this.filas().filter((f) => !f.terminada),
  );
  protected readonly terminadas = computed(() =>
    this.filas().filter((f) => f.terminada),
  );

  protected readonly kpis = computed(() => {
    const todas = this.filas();
    const enCurso = todas.filter((f) => !f.terminada);
    return [
      {
        etiqueta: 'En curso',
        valor: enCurso.length,
        icono: 'pending',
        color: 'blue' as CardColor,
        ayuda: 'Acciones con alguna luminaria que todavía no tiene el perfil.',
      },
      {
        etiqueta: 'Luminarias esperando',
        valor: todas.reduce((s, f) => s + f.a.enCurso, 0),
        icono: 'hourglass_top',
        color: 'indigo' as CardColor,
        ayuda:
          'Suma de las luminarias que todavía no tienen el perfil que se les mandó.',
      },
      {
        etiqueta: 'Luminarias detenidas',
        valor: todas.reduce((s, f) => s + f.a.detenido, 0),
        icono: 'flag',
        color: 'orange' as CardColor,
        ayuda:
          'Se les mandó el perfil las veces previstas y no quedó aplicado. Piden que alguien mire el nodo.',
      },
      {
        etiqueta: 'Aplicadas',
        valor: todas.filter((f) => f.terminada && !f.a.detenido).length,
        icono: 'task_alt',
        color: 'green' as CardColor,
        ayuda: 'Acciones en las que todas las luminarias ya tienen el perfil.',
      },
    ];
  });

  protected cuando(iso: string): string {
    return haceCuanto(iso);
  }

  protected fecha(iso: string): string {
    return fechaHora(iso);
  }

  protected ancho(f: Fila, clave: keyof AvanceAplicacion): number {
    return f.r.total ? (f.a[clave] / f.r.total) * 100 : 0;
  }

  protected recargar(): void {
    this.aplicaciones.reload();
  }

  protected cambiarHijos(ver: boolean): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { hijos: ver ? null : '0' },
      queryParamsHandling: 'merge',
    });
  }

  private fila(r: Resumen): Fila {
    const a = avanceResumen(r);
    const e = estadoAplicacion(a);
    const sinMarca = esSinMarca(r.id);
    const motivo = r.aplicacion
      ? MOTIVOS_APLICACION[r.aplicacion.motivo]
      : undefined;
    const idCliente = sinMarca ? clienteSinMarca(r.id) : r.idCliente;
    return {
      r,
      a,
      titulo: sinMarca
        ? this.tituloSinMarca(idCliente)
        : queSeHizo(r.aplicacion),
      icono: motivo?.icono ?? 'history',
      motivo: motivo?.etiqueta ?? 'Estado actual',
      motivoDetalle:
        motivo?.detalle ??
        'Configuraciones que quedaron pendientes de antes de que existiera el seguimiento.',
      quien: this.quien(r.aplicacion?.idUsuario),
      cliente: this.nombres.nombreCliente(idCliente),
      jerarquia: this.nombres.jerarquiaCliente(idCliente),
      porcentaje: r.total ? Math.floor((a.aplicado / r.total) * 100) : 0,
      tono: e.tono,
      estado: e.titulo,
      iconoEstado: e.icono,
      terminada: e.terminada,
      sinMarca,
    };
  }

  private tituloSinMarca(idCliente: string): string {
    const cliente = this.nombres.nombreCliente(idCliente);
    return cliente
      ? `Configuraciones pendientes de antes del seguimiento · ${cliente}`
      : 'Configuraciones pendientes de antes del seguimiento';
  }

  private quien(id: string | undefined): string | undefined {
    if (!id) return undefined;
    return nombreUsuario(this.usuarios.value()?.get(id));
  }
}
